import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2, Check, ArrowLeft, RefreshCw, Building2, Clock, Lock, Eye, EyeOff, LogOut, BarChart3, Users, MessageSquare, Download, Send, TrendingUp, FileText, Plus, Edit2, X, Globe, Briefcase, HelpCircle, Settings, BookOpen, Save, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_REPLIES = [
  { label: "Danke & Rückmeldung", subject: "Re: Ihre Anfrage bei Design Vision", body: "Vielen Dank für Ihre Anfrage! Ich melde mich innerhalb von 24 Stunden bei Ihnen.\n\nMit freundlichen Grüßen,\nBastian Rinsche\nDesign Vision" },
  { label: "Angebot-Gespräch", subject: "Re: Ihre Anfrage bei Design Vision", body: "Gerne erstelle ich Ihnen ein kostenloses Angebot. Wann passt ein kurzes Telefonat?\n\nSie erreichen mich auch unter +49 155 67148637 (WhatsApp).\n\nMit freundlichen Grüßen,\nBastian Rinsche\nDesign Vision" },
  { label: "Projektdetails", subject: "Re: Ihre Anfrage bei Design Vision", body: "Vielen Dank für Ihr Interesse! Um Ihnen ein passendes Angebot zu erstellen, benötige ich noch einige Details:\n\n- Welche Art von Website benötigen Sie?\n- Haben Sie bereits ein Design oder Logo?\n- Wann soll das Projekt fertig sein?\n\nMit freundlichen Grüßen,\nBastian Rinsche\nDesign Vision" }
];

const MiniChart = ({ data, color = "#d987ff" }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1">
          <div className="w-full rounded-sm transition-all" style={{ height: `${Math.max((d.value / maxVal) * 100, 4)}%`, backgroundColor: color, opacity: 0.6 + (i / data.length) * 0.4, minHeight: '2px' }} title={`${d.label}: ${d.value}`} />
        </div>
      ))}
    </div>
  );
};

// Reusable form field
const Field = ({ label, value, onChange, multiline, placeholder, required }) => (
  <div>
    <Label className="text-sm dark:text-gray-300">{label}{required && ' *'}</Label>
    {multiline ? (
      <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className="mt-1 dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
    ) : (
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1 dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
    )}
  </div>
);

// Content item card
const ContentCard = ({ item, title, subtitle, onEdit, onDelete }) => (
  <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-grey/20 dark:border-gray-800 flex items-center justify-between gap-4">
    <div className="min-w-0 flex-1">
      <p className="font-medium text-black dark:text-white truncate">{title}</p>
      {subtitle && <p className="text-sm text-mid-grey dark:text-gray-500 truncate">{subtitle}</p>}
    </div>
    <div className="flex gap-2 flex-shrink-0">
      <button onClick={onEdit} className="p-2 text-mid-purple hover:bg-mid-purple/10 rounded-lg transition-colors"><Edit2 size={16} /></button>
      <button onClick={onDelete} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors"><Trash2 size={16} /></button>
    </div>
  </div>
);

const AdminPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Content states
  const [references, setReferences] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) { verifyToken(token); } else { setLoading(false); }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
      setIsAuthenticated(true);
      fetchAll(token);
    } catch { localStorage.removeItem('admin_token'); setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
      localStorage.setItem('admin_token', r.data.token);
      setIsAuthenticated(true);
      fetchAll(r.data.token);
      toast.success('Erfolgreich eingeloggt');
    } catch { toast.error('Falsche Anmeldedaten'); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = () => { localStorage.removeItem('admin_token'); setIsAuthenticated(false); setMessages([]); setSelectedMessage(null); setStats(null); setPassword(''); setEmail(''); };
  const getAuth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }), []);

  const fetchAll = async (token) => {
    const h = { headers: { Authorization: `Bearer ${token || localStorage.getItem('admin_token')}` } };
    setLoading(true);
    try {
      const [msgs, st, refs, fqs, blogs, settings] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/messages`, h),
        axios.get(`${BACKEND_URL}/api/admin/stats`, h),
        axios.get(`${BACKEND_URL}/api/admin/references`, h),
        axios.get(`${BACKEND_URL}/api/admin/faqs`, h),
        axios.get(`${BACKEND_URL}/api/admin/blog`, h),
        axios.get(`${BACKEND_URL}/api/admin/settings`, h)
      ]);
      setMessages(msgs.data); setStats(st.data); setReferences(refs.data);
      setFaqs(fqs.data); setBlogPosts(blogs.data); setSiteSettings(settings.data);
    } catch (error) {
      if (error.response?.status === 401) { handleLogout(); toast.error('Sitzung abgelaufen.'); }
    } finally { setLoading(false); }
  };

  const markAsRead = async (id) => { try { await axios.patch(`${BACKEND_URL}/api/admin/messages/${id}/read`, {}, getAuth()); setMessages(p => p.map(m => m.id === id ? { ...m, read: true } : m)); toast.success('Gelesen'); } catch { toast.error('Fehler'); } };
  const deleteMessage = async (id) => { if (!window.confirm('Nachricht löschen?')) return; try { await axios.delete(`${BACKEND_URL}/api/admin/messages/${id}`, getAuth()); setMessages(p => p.filter(m => m.id !== id)); setSelectedMessage(null); toast.success('Gelöscht'); } catch { toast.error('Fehler'); } };
  const exportCSV = async () => { try { const r = await axios.get(`${BACKEND_URL}/api/admin/messages/export`, { ...getAuth(), responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'nachrichten_export.csv'; document.body.appendChild(a); a.click(); a.remove(); toast.success('CSV heruntergeladen'); } catch { toast.error('Fehler'); } };
  const searchMessages = async (q) => { setSearchQuery(q); try { const r = await axios.get(`${BACKEND_URL}/api/admin/messages${q ? `?search=${encodeURIComponent(q)}` : ''}`, getAuth()); setMessages(r.data); } catch { /* silent */ } };
  const changePassword = async (currentPw, newPw) => { try { await axios.put(`${BACKEND_URL}/api/admin/password`, { current_password: currentPw, new_password: newPw }, getAuth()); toast.success('Passwort geändert! Bitte neu einloggen.'); handleLogout(); } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Ändern'); } };
  const formatDate = (d) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Content CRUD
  const saveItem = async (type, data) => {
    try {
      if (data.id) {
        await axios.put(`${BACKEND_URL}/api/admin/${type}/${data.id}`, data, getAuth());
        toast.success('Gespeichert');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/${type}`, data, getAuth());
        toast.success('Erstellt');
      }
      setEditItem(null); setEditType(null);
      fetchAll();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/admin/${type}/${id}`, getAuth()); toast.success('Gelöscht'); fetchAll(); } catch { toast.error('Fehler'); }
  };

  const saveSettings = async (data) => {
    try { await axios.put(`${BACKEND_URL}/api/admin/settings`, data, getAuth()); setSiteSettings(data); toast.success('Einstellungen gespeichert'); } catch { toast.error('Fehler'); }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  // Login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-6">
        <Toaster position="top-right" richColors />
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-grey/10 dark:border-gray-800 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4 shadow-lg"><img src="/logo.jpg" alt="DV" className="w-full h-full object-cover" /></div>
            <h1 className="font-display text-2xl font-bold text-black dark:text-white">Admin Login</h1>
            <p className="text-dark-grey dark:text-gray-400 text-sm mt-2">Bitte geben Sie Ihr Passwort ein</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="dark:text-gray-300">E-Mail</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ihre@email.de" required className="dark:bg-gray-800 dark:border-gray-700 dark:text-white" data-testid="admin-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="dark:text-gray-300">Passwort</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="**********" required className="pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-white" data-testid="admin-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-grey dark:text-gray-400">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <Button type="submit" disabled={loginLoading} className="w-full bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-gray-100 text-white dark:text-black rounded-full py-6" data-testid="admin-login-btn">{loginLoading ? 'Wird überprüft...' : 'Einloggen'}</Button>
          </form>
          <div className="mt-6 text-center"><Link to="/" className="text-sm text-dark-grey dark:text-gray-400 hover:text-black dark:hover:text-white">&larr; Zurück zur Website</Link></div>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
    { id: 'messages', icon: MessageSquare, label: `Nachrichten${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
    { id: 'references', icon: Globe, label: 'Referenzen' },
    { id: 'blog', icon: BookOpen, label: 'Blog' },
    { id: 'faqs', icon: HelpCircle, label: 'FAQ' },
    { id: 'settings', icon: Settings, label: 'Einstellungen' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-grey/20 dark:border-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-dark-grey dark:text-gray-400 hover:text-black dark:hover:text-white"><ArrowLeft size={20} /></Link>
            <div className="flex items-center gap-2"><img src="/logo.jpg" alt="DV" className="w-8 h-8 rounded-lg object-cover" /><h1 className="font-display text-xl font-bold text-black dark:text-white">Admin Panel</h1></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchAll()} className="gap-2 dark:border-gray-700 dark:text-gray-300" data-testid="refresh-btn"><RefreshCw size={16} /><span className="hidden sm:inline">Aktualisieren</span></Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 dark:border-gray-700" data-testid="logout-btn"><LogOut size={16} /><span className="hidden sm:inline">Ausloggen</span></Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-900 border-b border-grey/20 dark:border-gray-800 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-mid-purple text-mid-purple' : 'border-transparent text-dark-grey dark:text-gray-400 hover:text-black dark:hover:text-white'}`} data-testid={`tab-${tab.id}`}>
                <tab.icon size={16} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && <DashboardView stats={stats} messages={messages} />}
        {activeTab === 'messages' && <MessagesView messages={messages} loading={loading} selectedMessage={selectedMessage} setSelectedMessage={setSelectedMessage} markAsRead={markAsRead} deleteMessage={deleteMessage} exportCSV={exportCSV} formatDate={formatDate} showQuickReply={showQuickReply} setShowQuickReply={setShowQuickReply} unreadCount={unreadCount} searchQuery={searchQuery} searchMessages={searchMessages} />}
        {activeTab === 'references' && <ContentManager type="references" items={references} title="Referenz-Projekte" fields={refFields} onSave={saveItem} onDelete={deleteItem} editItem={editItem} setEditItem={setEditItem} editType={editType} setEditType={setEditType} />}
        {activeTab === 'blog' && <BlogManager posts={blogPosts} onSave={saveItem} onDelete={deleteItem} editItem={editItem} setEditItem={setEditItem} editType={editType} setEditType={setEditType} />}
        {activeTab === 'faqs' && <ContentManager type="faqs" items={faqs} title="FAQ-Einträge" fields={faqFields} onSave={saveItem} onDelete={deleteItem} editItem={editItem} setEditItem={setEditItem} editType={editType} setEditType={setEditType} />}
        {activeTab === 'settings' && <SettingsView settings={siteSettings} onSave={saveSettings} onChangePassword={changePassword} />}
      </div>

      {/* Edit Modal */}
      {editItem && editType && editType !== 'blog' && (
        <EditModal type={editType} item={editItem} fields={editType === 'references' ? refFields : editType === 'faqs' ? faqFields : []} onSave={saveItem} onClose={() => { setEditItem(null); setEditType(null); }} />
      )}
      {editItem && editType === 'blog' && (
        <BlogEditModal item={editItem} onSave={saveItem} onClose={() => { setEditItem(null); setEditType(null); }} />
      )}
    </div>
  );
};

// Field definitions
const refFields = [
  { key: 'title', label: 'Titel', required: true },
  { key: 'url', label: 'URL', required: true },
  { key: 'image', label: 'Bild-URL' },
  { key: 'description_de', label: 'Beschreibung (DE)', multiline: true },
  { key: 'description_en', label: 'Beschreibung (EN)', multiline: true },
  { key: 'description_ar', label: 'Beschreibung (AR)', multiline: true },
  { key: 'features_de', label: 'Features DE (kommagetrennt)' },
  { key: 'features_en', label: 'Features EN (kommagetrennt)' },
  { key: 'features_ar', label: 'Features AR (kommagetrennt)' },
];

const faqFields = [
  { key: 'question_de', label: 'Frage (DE)', required: true },
  { key: 'answer_de', label: 'Antwort (DE)', multiline: true, required: true },
  { key: 'question_en', label: 'Frage (EN)' },
  { key: 'answer_en', label: 'Antwort (EN)', multiline: true },
  { key: 'question_ar', label: 'Frage (AR)' },
  { key: 'answer_ar', label: 'Antwort (AR)', multiline: true },
];

// Dashboard
const DashboardView = ({ stats, messages }) => {
  if (!stats) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-mid-purple border-t-transparent rounded-full" /></div>;
  const vc = stats.views_per_day.map(d => ({ label: d.date.slice(5), value: d.views }));
  const mc = stats.messages_per_day.map(d => ({ label: d.date.slice(5), value: d.count }));
  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SC icon={Users} label="Besucher heute" value={stats.views_today} color="bg-mid-blue/10 text-mid-blue" />
        <SC icon={TrendingUp} label="Besucher (7 Tage)" value={stats.views_week} color="bg-mid-green/10 text-mid-green" />
        <SC icon={MessageSquare} label="Nachrichten" value={stats.total_messages} color="bg-mid-purple/10 text-mid-purple" />
        <SC icon={Mail} label="Ungelesen" value={stats.unread_messages} color="bg-mid-orange/10 text-mid-orange" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
          <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><Users size={18} className="text-mid-blue" />Seitenaufrufe (14 Tage)</h3>
          <MiniChart data={vc} color="#88a2ff" />
          <div className="mt-4 pt-4 border-t border-grey/10 dark:border-gray-800 flex gap-6">
            <div><p className="text-2xl font-bold text-black dark:text-white">{stats.views_month}</p><p className="text-xs text-mid-grey">30 Tage</p></div>
            <div><p className="text-2xl font-bold text-black dark:text-white">{stats.total_views}</p><p className="text-xs text-mid-grey">Gesamt</p></div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
          <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><MessageSquare size={18} className="text-mid-purple" />Anfragen (14 Tage)</h3>
          <MiniChart data={mc} color="#d987ff" />
          {stats.top_pages?.length > 0 && <div className="mt-4 pt-4 border-t border-grey/10 dark:border-gray-800"><p className="text-xs text-mid-grey mb-2">Top-Seiten</p>{stats.top_pages.map((p, i) => <div key={i} className="flex justify-between py-1"><span className="text-sm text-dark-grey dark:text-gray-400 truncate">{p.page}</span><span className="text-sm font-medium text-black dark:text-white ml-2">{p.views}</span></div>)}</div>}
        </div>
      </div>
      {messages.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
          <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><FileText size={18} className="text-mid-green" />Letzte Nachrichten</h3>
          <div className="space-y-3">{messages.slice(0, 5).map(msg => <div key={msg.id} className={`flex items-center gap-4 p-3 rounded-xl ${!msg.read ? 'bg-mid-purple/5' : 'bg-grey/5 dark:bg-gray-800'}`}><div className={`w-2 h-2 rounded-full ${!msg.read ? 'bg-mid-purple' : 'bg-grey'}`} /><div className="flex-1 min-w-0"><p className="font-medium text-sm text-black dark:text-white truncate">{msg.name}</p><p className="text-xs text-mid-grey truncate">{msg.message}</p></div><span className="text-xs text-mid-grey">{new Date(msg.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span></div>)}</div>
        </div>
      )}
    </div>
  );
};

const SC = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-5">
    <div className={`w-10 h-10 rounded-xl ${color.split(' ')[0]} flex items-center justify-center mb-3`}><Icon size={20} className={color.split(' ')[1]} /></div>
    <p className="text-3xl font-bold text-black dark:text-white">{value}</p>
    <p className="text-sm text-mid-grey mt-1">{label}</p>
  </div>
);

// Messages View
const MessagesView = ({ messages, loading, selectedMessage, setSelectedMessage, markAsRead, deleteMessage, exportCSV, formatDate, showQuickReply, setShowQuickReply, unreadCount, searchQuery, searchMessages }) => (
  <div data-testid="messages-view">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <p className="text-sm text-dark-grey dark:text-gray-400">{messages.length} Nachrichten{unreadCount > 0 && <span className="ml-1 text-mid-purple font-medium">({unreadCount} ungelesen)</span>}</p>
      <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2 dark:border-gray-700 dark:text-gray-300" data-testid="export-csv-btn"><Download size={16} />CSV Export</Button>
    </div>
    <div className="mb-6">
      <Input placeholder="Nachrichten durchsuchen..." value={searchQuery} onChange={e => searchMessages(e.target.value)} className="dark:bg-gray-900 dark:border-gray-800 dark:text-white" data-testid="message-search" />
    </div>
    {loading ? <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-mid-purple border-t-transparent rounded-full" /></div>
    : messages.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800"><Mail size={48} className="mx-auto text-grey dark:text-gray-600 mb-4" /><h2 className="text-xl font-semibold text-dark-grey dark:text-gray-400">Keine Nachrichten</h2></div>
    : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">{messages.map(msg => (
          <div key={msg.id} onClick={() => { setSelectedMessage(msg); if (!msg.read) markAsRead(msg.id); }} className={`p-4 bg-white dark:bg-gray-900 rounded-xl border cursor-pointer transition-all hover:shadow-md ${selectedMessage?.id === msg.id ? 'border-mid-purple shadow-md' : 'border-grey/20 dark:border-gray-800'} ${!msg.read ? 'border-l-4 border-l-mid-purple' : ''}`}>
            <div className="flex items-start justify-between mb-2"><h3 className={`font-semibold ${!msg.read ? 'text-black dark:text-white' : 'text-dark-grey dark:text-gray-400'}`}>{msg.name}</h3>{!msg.read && <span className="w-2 h-2 bg-mid-purple rounded-full" />}</div>
            <p className="text-sm text-mid-grey truncate">{msg.email}</p><p className="text-sm text-dark-grey dark:text-gray-400 mt-2 line-clamp-2">{msg.message}</p>
            <p className="text-xs text-mid-grey mt-2 flex items-center gap-1"><Clock size={12} />{formatDate(msg.created_at)}</p>
          </div>
        ))}</div>
        <div className="lg:col-span-2">{selectedMessage ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-grey/20 dark:border-gray-800 p-6">
            <div className="flex items-start justify-between mb-6">
              <div><h2 className="text-2xl font-display font-bold text-black dark:text-white">{selectedMessage.name}</h2><a href={`mailto:${selectedMessage.email}`} className="text-mid-purple hover:underline">{selectedMessage.email}</a>{selectedMessage.company && <p className="text-dark-grey dark:text-gray-400 flex items-center gap-2 mt-1"><Building2 size={14} />{selectedMessage.company}</p>}</div>
              <div className="flex gap-2">{!selectedMessage.read && <Button variant="outline" size="sm" onClick={() => markAsRead(selectedMessage.id)} className="gap-1 dark:border-gray-700"><Check size={14} />Gelesen</Button>}<Button variant="outline" size="sm" onClick={() => deleteMessage(selectedMessage.id)} className="gap-1 text-red-500 dark:border-gray-700"><Trash2 size={14} />Löschen</Button></div>
            </div>
            <div className="border-t border-grey/20 dark:border-gray-800 pt-6">
              <p className="text-xs text-mid-grey mb-2 flex items-center gap-1"><Clock size={12} />Empfangen am {formatDate(selectedMessage.created_at)}</p>
              <div className="bg-grey/5 dark:bg-gray-800 rounded-lg p-4"><p className="text-black dark:text-white whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p></div>
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => setShowQuickReply(!showQuickReply)} className="bg-black dark:bg-white text-white dark:text-black rounded-full" data-testid="quick-reply-toggle"><Send size={16} className="mr-2" />Schnellantwort</Button>
                <Button variant="outline" onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: Ihre Anfrage bei Design Vision`} className="rounded-full dark:border-gray-700 dark:text-gray-300"><Mail size={16} className="mr-2" />Eigene E-Mail</Button>
              </div>
              {showQuickReply && <div className="bg-grey/5 dark:bg-gray-800 rounded-xl p-4 space-y-2" data-testid="quick-reply-panel"><p className="text-sm font-medium text-black dark:text-white mb-3">Vorlage auswählen:</p>{QUICK_REPLIES.map((t, i) => <button key={i} onClick={() => { window.location.href = `mailto:${selectedMessage.email}?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`; setShowQuickReply(false); }} className="w-full text-left p-3 bg-white dark:bg-gray-900 rounded-lg border border-grey/20 dark:border-gray-700 hover:border-mid-purple/30 transition-colors" data-testid={`quick-reply-${i}`}><p className="font-medium text-sm text-black dark:text-white">{t.label}</p><p className="text-xs text-mid-grey mt-1 line-clamp-2">{t.body.split('\n')[0]}</p></button>)}</div>}
            </div>
          </div>
        ) : <div className="bg-white dark:bg-gray-900 rounded-xl border border-grey/20 dark:border-gray-800 p-12 text-center"><Mail size={48} className="mx-auto text-grey/50 dark:text-gray-700 mb-4" /><p className="text-dark-grey dark:text-gray-400">Wählen Sie eine Nachricht aus.</p></div>}</div>
      </div>
    )}
  </div>
);

// Generic Content Manager
const ContentManager = ({ type, items, title, fields, onSave, onDelete, editItem, setEditItem, editType, setEditType }) => (
  <div data-testid={`manage-${type}`}>
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-xl font-bold text-black dark:text-white">{title} ({items.length})</h2>
      <Button onClick={() => { setEditItem({}); setEditType(type); }} className="gap-2 bg-black dark:bg-white text-white dark:text-black rounded-full" data-testid={`add-${type}-btn`}><Plus size={16} />Hinzufügen</Button>
    </div>
    {items.length === 0 ? <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800"><p className="text-dark-grey dark:text-gray-400">Noch keine Einträge vorhanden.</p></div>
    : <div className="space-y-3">{items.map(item => <ContentCard key={item.id} item={item} title={item.title || item.question_de || item.title_de || 'Untitled'} subtitle={item.url || item.answer_de?.slice(0, 60) || ''} onEdit={() => { setEditItem(item); setEditType(type); }} onDelete={() => onDelete(type, item.id)} />)}</div>}
  </div>
);

// Blog Manager
const BlogManager = ({ posts, onSave, onDelete, editItem, setEditItem, editType, setEditType }) => (
  <div data-testid="manage-blog">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-xl font-bold text-black dark:text-white">Blog-Beiträge ({posts.length})</h2>
      <Button onClick={() => { setEditItem({}); setEditType('blog'); }} className="gap-2 bg-black dark:bg-white text-white dark:text-black rounded-full" data-testid="add-blog-btn"><Plus size={16} />Neuer Beitrag</Button>
    </div>
    {posts.length === 0 ? <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800"><p className="text-dark-grey dark:text-gray-400">Noch keine Beiträge.</p></div>
    : <div className="space-y-3">{posts.map(p => <ContentCard key={p.id} item={p} title={p.title_de} subtitle={`${p.published ? 'Veröffentlicht' : 'Entwurf'} | ${p.tags?.join(', ') || ''}`} onEdit={() => { setEditItem(p); setEditType('blog'); }} onDelete={() => onDelete('blog', p.id)} />)}</div>}
  </div>
);

// Edit Modal
const EditModal = ({ type, item, fields, onSave, onClose }) => {
  const [form, setForm] = useState({ ...item });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSave = () => {
    const data = { ...form };
    // Convert comma-separated strings to arrays for feature fields
    ['features_de', 'features_en', 'features_ar'].forEach(k => {
      if (typeof data[k] === 'string') data[k] = data[k].split(',').map(s => s.trim()).filter(Boolean);
    });
    onSave(type, data);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-grey/20 dark:border-gray-800 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl font-bold text-black dark:text-white">{item.id ? 'Bearbeiten' : 'Neu erstellen'}</h3>
          <button onClick={onClose} className="text-dark-grey hover:text-black dark:hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {fields.map(f => (
            <Field key={f.key} label={f.label} required={f.required} multiline={f.multiline} value={Array.isArray(form[f.key]) ? form[f.key].join(', ') : form[f.key]} onChange={v => set(f.key, v)} />
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={handleSave} className="flex-1 bg-black dark:bg-white text-white dark:text-black rounded-full"><Save size={16} className="mr-2" />Speichern</Button>
          <Button variant="outline" onClick={onClose} className="rounded-full dark:border-gray-700 dark:text-gray-300">Abbrechen</Button>
        </div>
      </div>
    </div>
  );
};

// Blog Edit Modal
const BlogEditModal = ({ item, onSave, onClose }) => {
  const [form, setForm] = useState({ published: true, tags: [], ...item, tags_str: item.tags?.join(', ') || '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSave = () => {
    const data = { ...form, tags: form.tags_str.split(',').map(s => s.trim()).filter(Boolean) };
    delete data.tags_str;
    onSave('blog', data);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-grey/20 dark:border-gray-800 p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl font-bold text-black dark:text-white">{item.id ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}</h3>
          <button onClick={onClose} className="text-dark-grey hover:text-black dark:hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Bild-URL" value={form.image} onChange={v => set('image', v)} placeholder="https://..." />
            <Field label="Tags (kommagetrennt)" value={form.tags_str} onChange={v => set('tags_str', v)} placeholder="Webdesign, SEO" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={e => set('published', e.target.checked)} id="pub" className="rounded" />
            <Label htmlFor="pub" className="dark:text-gray-300">Veröffentlicht</Label>
          </div>
          <hr className="dark:border-gray-800" />
          <p className="text-sm font-medium text-mid-purple">Deutsch</p>
          <Field label="Titel (DE)" value={form.title_de} onChange={v => set('title_de', v)} required />
          <Field label="Auszug (DE)" value={form.excerpt_de} onChange={v => set('excerpt_de', v)} multiline />
          <Field label="Inhalt (DE) - Markdown" value={form.content_de} onChange={v => set('content_de', v)} multiline />
          <hr className="dark:border-gray-800" />
          <p className="text-sm font-medium text-mid-blue">English</p>
          <Field label="Title (EN)" value={form.title_en} onChange={v => set('title_en', v)} required />
          <Field label="Excerpt (EN)" value={form.excerpt_en} onChange={v => set('excerpt_en', v)} multiline />
          <Field label="Content (EN) - Markdown" value={form.content_en} onChange={v => set('content_en', v)} multiline />
          <hr className="dark:border-gray-800" />
          <p className="text-sm font-medium text-mid-orange">العربية</p>
          <Field label="العنوان (AR)" value={form.title_ar} onChange={v => set('title_ar', v)} />
          <Field label="المقتطف (AR)" value={form.excerpt_ar} onChange={v => set('excerpt_ar', v)} multiline />
          <Field label="المحتوى (AR)" value={form.content_ar} onChange={v => set('content_ar', v)} multiline />
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={handleSave} className="flex-1 bg-black dark:bg-white text-white dark:text-black rounded-full"><Save size={16} className="mr-2" />Speichern</Button>
          <Button variant="outline" onClick={onClose} className="rounded-full dark:border-gray-700 dark:text-gray-300">Abbrechen</Button>
        </div>
      </div>
    </div>
  );
};

// Settings View
const SettingsView = ({ settings, onSave, onChangePassword }) => {
  const [form, setForm] = useState({ ...settings });
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-6" data-testid="settings-view">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><Mail size={18} className="text-mid-purple" />Kontaktdaten</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="E-Mail" value={form.email} onChange={v => set('email', v)} placeholder="info@example.com" />
          <Field label="Telefon" value={form.phone} onChange={v => set('phone', v)} placeholder="+49..." />
          <Field label="WhatsApp-Nummer" value={form.whatsapp} onChange={v => set('whatsapp', v)} placeholder="4915567148637" />
          <Field label="Adresse" value={form.address} onChange={v => set('address', v)} placeholder="Straße, PLZ Ort" />
        </div>
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><Briefcase size={18} className="text-mid-green" />Über mich</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" value={form.about_name} onChange={v => set('about_name', v)} placeholder="Bastian Rinsche" />
            <Field label="Jobtitel (DE)" value={form.about_title_de} onChange={v => set('about_title_de', v)} placeholder="Gründer & Webentwickler" />
          </div>
          <Field label="Bio (DE)" value={form.about_bio_de} onChange={v => set('about_bio_de', v)} multiline />
          <Field label="Bio (EN)" value={form.about_bio_en} onChange={v => set('about_bio_en', v)} multiline />
          <Field label="Bio (AR)" value={form.about_bio_ar} onChange={v => set('about_bio_ar', v)} multiline />
          <Field label="Skills (kommagetrennt)" value={form.about_skills} onChange={v => set('about_skills', v)} placeholder="Webdesign, E-Commerce, ..." />
        </div>
      </div>
      <Button onClick={() => onSave(form)} className="bg-black dark:bg-white text-white dark:text-black rounded-full px-8" data-testid="save-settings-btn"><Save size={16} className="mr-2" />Einstellungen speichern</Button>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-grey/20 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-black dark:text-white mb-4 flex items-center gap-2"><Lock size={18} className="text-mid-orange" />Passwort ändern</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Aktuelles Passwort" value={currentPw} onChange={setCurrentPw} placeholder="********" />
          <Field label="Neues Passwort" value={newPw} onChange={setNewPw} placeholder="********" />
        </div>
        <Button onClick={() => { if (currentPw && newPw) onChangePassword(currentPw, newPw); else toast.error('Bitte beide Felder ausfüllen'); }} variant="outline" className="mt-4 gap-2 dark:border-gray-700 dark:text-gray-300" data-testid="change-password-btn"><Lock size={16} />Passwort ändern</Button>
      </div>
    </div>
  );
};

export default AdminPage;
